/**
 * PrivateRoute component for protected routes.
 *
 * Redirects to login page if user is not authenticated.
 * Feature 016: Redirects to onboarding if incomplete (T022-T023).
 * Shows loading state while checking authentication status.
 */

import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface PrivateRouteProps {
  children: ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Feature 016: T022-T023 - Enforce onboarding completion
  // Skip onboarding check if already on onboarding or welcome page
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  const isWelcomePage = location.pathname === '/welcome';

  if (!isOnboardingRoute && !isWelcomePage && user?.onboarding) {
    const { completed, dismissed, current_page, pages_completed } = user.onboarding;

    // If onboarding not completed and not dismissed, redirect to appropriate page
    if (!completed && !dismissed) {
      // If all 4 pages are completed, show Welcome page
      const allPagesComplete = pages_completed?.length === 4 || current_page === 4;
      if (allPagesComplete) {
        return <Navigate to="/welcome" replace />;
      }

      // T023: Resume logic based on current_page
      const onboardingPages = [
        '/onboarding/personal-details', // current_page = 0 or 1
        '/onboarding/goals',             // current_page = 2
        '/onboarding/body-state',        // current_page = 3
        '/onboarding/struggles'          // current_page = 4
      ];

      // Map current_page to the correct onboarding route
      // 0 = not started → personal-details
      // 1 = personal-details page → personal-details
      // 2 = goals page → goals
      // 3 = body-state page → body-state
      // 4 = struggles page → struggles (shouldn't reach here if allPagesComplete)
      const targetPage = current_page === 0 ? 0 : Math.min(current_page - 1, 3);
      return <Navigate to={onboardingPages[targetPage]} replace />;
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;
